import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, Heart, User, Search, X, Home, Gift, Info, MessageCircle, LogIn, LogOut, Settings, UtensilsCrossed } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useCart } from "../../context/CartContext";
import { useWishlist } from "../../context/WishlistContext";
import { useAuth } from "../../context/AuthContext";
import { fetchProducts } from "../../lib/api";
import { Sparkle, Heart as DoodleHeart, Star } from "../doodles/Doodles";
const NAV = [
  { to: "/", label: "Home", icon: Home },
  { to: "/shop/her", label: "Gifts for Her", icon: Gift },
  { to: "/shop/him", label: "Gifts for Him", icon: Gift },
  { to: "/shop/kids", label: "Gifts for Kids", icon: Gift },
  { to: "/shop/babies", label: "Gifts for Babies", icon: Gift },
  { to: "/shop/baskets", label: "Baskets Collection", icon: Gift },
  { to: "/shop/diwali", label: "Diwali Hampers", icon: Gift, badge: "Soon" },
  { to: "/table-grazing", label: "Table Grazing - Hyderabad", icon: UtensilsCrossed, badge: "Soon" },
  { to: "/about", label: "About Us", icon: Info },
  { to: "/contact", label: "Contact", icon: MessageCircle },
];

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const { count } = useCart();
  const { count: wishCount } = useWishlist();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const menuRef = useRef(null);

  // Close menus on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Close search on Esc + lock body scroll while open
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setSearchOpen(false); };
    if (searchOpen) {
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", onKey);
    }
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [searchOpen]);

  // Reset query when closing
  useEffect(() => { if (!searchOpen) { setQ(""); setResults([]); } }, [searchOpen]);

  // Search debounce
  useEffect(() => {
    if (!q) { setResults([]); return; }
    const t = setTimeout(() => fetchProducts({ q }).then(setResults).catch(() => setResults([])), 200);
    return () => clearTimeout(t);
  }, [q]);

  const goTo = (slug) => { setSearchOpen(false); navigate(`/product/${slug}`); };

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-40 bg-[#FFFAF3]/95 backdrop-blur-md border-b-2 border-[#2D2424]">
        <div className="container-x flex items-center justify-between h-24 gap-4">
          {/* LEFT — Hamburger Menu */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              data-testid="nav-menu-toggle"
              className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-[#2D2424] rounded-full font-hand text-sm"
              style={{ boxShadow: "3px 3px 0 #2D2424" }}
              aria-label="menu"
            >
              <Menu className="w-5 h-5" strokeWidth={2}/>
              <span className="hidden sm:inline">Menu</span>
            </button>
            {menuOpen && (
              <div
                data-testid="nav-menu-dropdown"
                className="absolute left-0 top-14 w-72 bg-white border-2 border-[#2D2424] rounded-2xl py-2 z-50"
                style={{ boxShadow: "5px 5px 0 #2D2424" }}
              >
                {NAV.map((n) => {
                  const Icon = n.icon;
                  return (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      onClick={() => setMenuOpen(false)}
                      data-testid={`menu-${n.label.toLowerCase().replace(/ /g, "-")}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-5 py-2.5 font-hand text-sm hover:bg-[#FFE7F1] ${isActive ? "bg-[#FFE7F1] text-[#7A1F2B]" : "text-[#2D2424]"}`
                      }
                    >
                      <Icon className="w-4 h-4 shrink-0" strokeWidth={2}/>
                      <span className="flex-1 truncate">{n.label}</span>
                      {n.badge && (
                        <span className="ml-auto text-[10px] font-doodle uppercase tracking-wider bg-[#FFD93D] text-[#7A1F2B] px-2 py-0.5 rounded-full border border-[#7A1F2B]">{n.badge}</span>
                      )}
                    </NavLink>
                  );
                })}
                <div className="border-t-2 border-[#2D2424]/10 mt-2 pt-2">
                  {user ? (
                    <>
                      <NavLink to="/account" onClick={() => setMenuOpen(false)} data-testid="menu-account" className="flex items-center gap-3 px-5 py-2.5 font-hand text-sm hover:bg-[#E7F4FF] text-[#2D2424]">
                        <Settings className="w-4 h-4"/> My Account
                      </NavLink>
                      <button onClick={() => { logout(); setMenuOpen(false); navigate("/"); }} data-testid="menu-logout" className="w-full flex items-center gap-3 px-5 py-2.5 font-hand text-sm hover:bg-[#FFE7F1] text-[#7A1F2B] text-left">
                        <LogOut className="w-4 h-4"/> Sign Out
                      </button>
                    </>
                  ) : (
                    <NavLink to="/login" onClick={() => setMenuOpen(false)} data-testid="menu-login" className="flex items-center gap-3 px-5 py-2.5 font-hand text-sm hover:bg-[#E7F4FF] text-[#2D2424]">
                      <LogIn className="w-4 h-4"/> Sign In / Sign Up
                    </NavLink>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* CENTER — Brand */}
          <Link to="/" className="flex-1 text-center min-w-0" data-testid="nav-logo">
            <div className="font-doodle text-2xl sm:text-3xl lg:text-4xl text-[#7A1F2B] leading-none truncate">Naathi Charami</div>
            <div className="text-[10px] sm:text-xs font-hand italic text-[#5C5555] mt-1 truncate">Gifts &amp; Hampers · by Monalika Reddy</div>
          </Link>

          {/* RIGHT — Icons */}
          <div className="flex items-center gap-3 sm:gap-4">
            {/* Search */}
            <button
              onClick={() => setSearchOpen(true)}
              data-testid="nav-search-toggle"
              className="text-[#2D2424] hover:text-[#7A1F2B]"
              aria-label="search"
            >
              <Search className="w-5 h-5" strokeWidth={2}/>
            </button>

            {/* Wishlist */}
            <Link to="/wishlist" data-testid="nav-wishlist" className="relative text-[#2D2424] hover:text-[#7A1F2B]" aria-label="wishlist">
              <Heart className="w-5 h-5" strokeWidth={2}/>
              {wishCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#7A1F2B] text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-medium">{wishCount}</span>
              )}
            </Link>

            {/* Login / Account */}
            <Link to={user ? "/account" : "/login"} data-testid="nav-account" className="text-[#2D2424] hover:text-[#7A1F2B] flex items-center gap-1.5" aria-label={user ? "account" : "login"}>
              <User className="w-5 h-5" strokeWidth={2}/>
              <span className="font-hand text-sm hidden md:inline">{user ? user.name.split(" ")[0] : "Login"}</span>
            </Link>

            {/* Cart */}
            <Link to="/cart" data-testid="nav-cart" className="relative text-[#2D2424] hover:text-[#7A1F2B]" aria-label="cart">
              <ShoppingBag className="w-5 h-5" strokeWidth={2}/>
              {count > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-2 -right-2 bg-[#7A1F2B] text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-medium">{count}</span>
              )}
            </Link>
          </div>
        </div>
      </header>

      {/* SEARCH OVERLAY — full-screen, doodle-themed */}
      {searchOpen && (
        <div
          data-testid="search-overlay"
          className="fixed inset-0 z-[100] flex items-start justify-center px-3 sm:px-6 pt-16 sm:pt-24 animate-search-fade"
          onClick={(e) => { if (e.target === e.currentTarget) setSearchOpen(false); }}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-[#2D2424]/55 backdrop-blur-sm" aria-hidden="true"/>

          {/* Floating doodles */}
          <Sparkle color="#FFD93D" className="absolute top-8 left-6 w-6 animate-twinkle pointer-events-none"/>
          <DoodleHeart color="#FF6B9D" className="absolute top-10 right-10 w-7 animate-bounce-doodle pointer-events-none"/>
          <Star color="#BAE1FF" className="absolute bottom-20 left-1/4 w-7 animate-spin-slow pointer-events-none hidden sm:block"/>

          {/* Panel */}
          <div
            className="relative w-full max-w-2xl bg-white border-2 border-[#2D2424] rounded-3xl overflow-hidden animate-search-pop"
            style={{ boxShadow: "8px 8px 0 #2D2424" }}
            role="dialog"
            aria-modal="true"
          >
            {/* Header row */}
            <div className="flex items-center gap-2 px-4 sm:px-5 py-3 border-b-2 border-[#2D2424]/15 bg-[#FFFAF3]">
              <Search className="w-5 h-5 text-[#7A1F2B] shrink-0" strokeWidth={2}/>
              <input
                autoFocus
                placeholder="Search hampers, rakhis, gifts…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="search-input"
                className="flex-1 min-w-0 px-2 py-2 text-base sm:text-lg font-hand text-[#2D2424] placeholder-[#9B9494] bg-transparent focus:outline-none"
              />
              {q && (
                <button
                  onClick={() => setQ("")}
                  data-testid="search-clear"
                  className="text-[#5C5555] hover:text-[#7A1F2B] shrink-0 p-1"
                  aria-label="clear"
                >
                  <X className="w-4 h-4"/>
                </button>
              )}
              <button
                onClick={() => setSearchOpen(false)}
                data-testid="search-close"
                className="ml-1 shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full border-2 border-[#2D2424] bg-white text-[#2D2424] hover:bg-[#FFE7F1]"
                style={{ boxShadow: "2px 2px 0 #2D2424" }}
                aria-label="close search"
              >
                <X className="w-4 h-4" strokeWidth={2.5}/>
              </button>
            </div>

            {/* Body */}
            <div className="max-h-[60vh] overflow-y-auto">
              {!q && (
                <div className="px-6 py-10 text-center">
                  <p className="font-doodle text-2xl text-[#7A1F2B]">What are you looking for? ✨</p>
                  <p className="mt-2 text-sm font-hand text-[#5C5555]">Try “rakhi”, “rakhi hampers”, “birthdays” or “occasions”…</p>
                  <div className="mt-5 flex flex-wrap justify-center gap-2">
                    {[
                      { label: "Diwali Hampers", to: "/shop/diwali" },
                      { label: "For Her", to: "/shop/her" },
                      { label: "Birthdays", q: "birthday" },
                      { label: "Baskets", to: "/shop/baskets" },
                    ].map((s) => (
                      <button
                        key={s.label}
                        onClick={() => {
                          if (s.to) {
                            setSearchOpen(false);
                            setQ("");
                            navigate(s.to);
                          } else {
                            setQ(s.q);
                          }
                        }}
                        data-testid={`search-suggest-${s.label.toLowerCase().replace(/ /g, "-")}`}
                        className="doodle-pill bg-[#FFE7F1] text-[#7A1F2B] hover:bg-[#FFD6E7]"
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {q && results.length === 0 && (
                <p className="px-6 py-10 text-center font-hand text-[#5C5555]">No matching gifts found 💭</p>
              )}
              {results.length > 0 && (
                <ul className="py-2">
                  {results.map((p) => (
                    <li key={p.id}>
                      <button
                        onClick={() => goTo(p.slug)}
                        data-testid={`search-result-${p.slug}`}
                        className="w-full flex items-center gap-3 px-4 sm:px-5 py-3 hover:bg-[#FFE7F1] text-left transition-colors"
                      >
                        <img src={p.images?.[0]} alt="" className="w-14 h-14 object-cover rounded-xl border-2 border-[#2D2424]/10"/>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-hand text-[#2D2424] truncate">{p.name}</p>
                          <p className="text-xs text-[#5C5555] truncate font-hand">{p.short_description || p.category}</p>
                        </div>
                        <p className="font-doodle text-lg text-[#7A1F2B] shrink-0">₹{p.price.toLocaleString("en-IN")}</p>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
